<template>
  <li
    class="px-4 py-2 border-l-4 hover:bg-gray-200 group"
    :class="borderClass"
  >
    <div class="flex items-center">
      <!-- Status -->
      <fa
        :icon="statusIcon"
        fixed-width
        :class="statusClass"
        :title="$t(`components.landingpages.materialSet.status.${setStatus}`)"
      />

      <!-- Name -->
      <div class="flex-1 px-4">
        {{ $t('components.landingpages.materialSet.title', { count: variants.length }) }}
        <span class="ml-1 text-xs text-gray-500">
          {{ $t(`components.landingpages.materialSet.status.${setStatus}`) }}
        </span>
      </div>

      <!-- Stable positions -->
      <div class="flex space-x-1">
        <span
          v-for="position in POSITIONS"
          :key="position"
          class="w-5 text-xs text-center border rounded"
          :class="positionClass(position)"
          :title="positionTitle(position)"
        >{{ position }}</span>
      </div>

      <!-- Actions -->
      <div class="flex justify-end pl-4 space-x-1 w-28">
        <button
          class="btn-icon"
          :title="$t('components.landingpages.materialSet.toggleDetail')"
          @click="isExpanded = !isExpanded"
        >
          <fa
            :icon="isExpanded ? faChevronUp : faChevronDown"
            fixed-width
          />
        </button>

        <button
          v-if="canApprove"
          class="btn-icon"
          :title="$t('components.landingpages.materialSet.approve')"
          @click="approveSet"
        >
          <fa
            :icon="faCheck"
            fixed-width
          />
        </button>

        <button
          v-if="canRetry"
          class="btn-icon"
          :title="$t('components.landingpages.materialSet.retry')"
          @click="retrySet"
        >
          <fa
            :icon="faSync"
            fixed-width
          />
        </button>

        <button
          v-if="canUnpublish"
          class="btn-icon"
          :title="$t('components.landingpages.materialSet.unpublish')"
          @click="unpublishSet"
        >
          <fa
            :icon="faEyeSlash"
            fixed-width
          />
        </button>
      </div>
    </div>

    <div
      v-if="isExpanded"
      class="mt-2 ml-8 text-xs text-gray-700"
    >
      <p v-if="$apollo.queries.materialSet.loading">
        {{ $t('components.landingpages.materialSet.loading') }}
      </p>

      <template v-else-if="materialSet">
        <p
          v-if="!materialSet.diagnosticsAvailable"
          class="text-yellow-700"
        >
          {{ $t('components.landingpages.materialSet.diagnosticsUnavailable') }}
        </p>

        <p>
          {{ $t('components.landingpages.materialSet.attempts', {
            count: materialSet.attemptCount,
            at: materialSet.lastAttemptAt ? formatDateTime(materialSet.lastAttemptAt) : '-'
          }) }}
        </p>

        <p
          v-if="['FAILED', 'BLOCKED'].includes(setStatus) && materialSet.lastFailureCode"
          class="text-red-700"
        >
          {{ $t('components.landingpages.materialSet.failure', {
            code: materialSet.lastFailureCode,
            reason: materialSet.failureReason || '-',
            message: materialSet.lastFailureMessage || '-'
          }) }}
        </p>

        <p
          v-if="materialSet.missingFields.length"
          class="text-yellow-700"
        >
          {{ $t('components.landingpages.materialSet.missingData', {
            fields: materialSet.missingFields.join(', ')
          }) }}
        </p>

        <ul class="mt-1">
          <li
            v-for="detail in materialSet.variants"
            :key="detail.materialId"
            class="flex space-x-2"
          >
            <span class="w-6">#{{ detail.variantIndex }}</span>
            <span class="w-32">{{ detail.status }}</span>
            <span v-if="detail.quality">
              {{ $t('components.landingpages.materialSet.quality', {
                errors: detail.quality.errorCount,
                warnings: detail.quality.warningCount
              }) }}
            </span>
            <span
              v-if="detail.blockedReasons.length"
              class="text-yellow-700"
            >
              {{ detail.blockedReasons.join(', ') }}
            </span>
            <a
              v-if="variantFile(detail.materialId)"
              :href="createAttachmentUrl(variantFile(detail.materialId))"
              target="_blank"
              class="btn-inline-small"
            >
              {{ $t('components.landingpages.materialSet.reviewVariant') }}
            </a>
          </li>
        </ul>
      </template>
    </div>
  </li>
</template>

<script>
import {
  faCheck,
  faClock,
  faSpinner,
  faExclamationTriangle,
  faEyeSlash,
  faSync,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons'
import role from '@/mixins/role'
import userFeedback from '@/mixins/userFeedback'
import eventBus, { eventNames } from '@/lib/eventBus'
import APPROVE_MATERIAL_SET from '@/graphql/ApproveMaterialSet.gql'
import GET_MATERIAL_SET from '@/graphql/GetMaterialSet.gql'
import MATERIALS from '@/graphql/material/materials.gql'
import RETRY_MATERIAL_SET from '@/graphql/RetryMaterialSet.gql'
import UNPUBLISH_MATERIAL_SET from '@/graphql/UnpublishMaterialSet.gql'

/**
 * One social image set, as one row.
 *
 * The five images are five separate materials, but they publish and withdraw
 * together. Rendering them as one row is what stops an operator from putting
 * the set into a mixed public state: there is no per-variant control here.
 */

const POSITIONS = [1, 2, 3, 4, 5]

const ICONS_BY_STATUS = {
  PENDING: faClock,
  GENERATING: faSpinner,
  BLOCKED: faExclamationTriangle,
  FAILED: faExclamationTriangle,
  READY: faCheck,
  PUBLISHED: faCheck
}

const CLASSES_BY_STATUS = {
  PENDING: 'text-gray-400',
  GENERATING: 'animate-spin',
  BLOCKED: 'text-yellow-500',
  FAILED: 'text-red-500',
  READY: 'text-blue-500',
  PUBLISHED: 'text-green-500'
}

const BORDERS_BY_STATUS = {
  PENDING: 'border-gray-300',
  GENERATING: 'border-gray-300',
  BLOCKED: 'border-yellow-400',
  FAILED: 'border-red-400',
  READY: 'border-blue-400',
  PUBLISHED: 'border-green-400'
}

export default {
  mixins: [role, userFeedback],

  props: {
    materialSetId: {
      type: String,
      required: true
    },
    variants: {
      type: Array,
      required: true
    },
    companyId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      isExpanded: false,
      materialSet: null,
      POSITIONS,
      faEyeSlash,
      faCheck,
      faSync,
      faChevronDown,
      faChevronUp
    }
  },

  apollo: {
    materialSet: {
      query: GET_MATERIAL_SET,
      variables () {
        return { materialSetId: this.materialSetId, companyId: this.companyId }
      },
      skip () {
        return !this.isExpanded
      },
      fetchPolicy: 'cache-and-network'
    }
  },

  computed: {
    isPublished () {
      return this.variants.some(({ publishedVersion }) => publishedVersion)
    },

    setStatus () {
      const stored = this.variants[0]?.materialMakerSetStatus || 'PENDING'
      return stored === 'READY' && this.isPublished ? 'PUBLISHED' : stored
    },

    statusIcon () {
      return ICONS_BY_STATUS[this.setStatus] || faClock
    },

    statusClass () {
      return CLASSES_BY_STATUS[this.setStatus] || 'text-gray-400'
    },

    borderClass () {
      return BORDERS_BY_STATUS[this.setStatus] || 'border-gray-300'
    },

    variantByPosition () {
      return new Map(this.variants.map(variant => [variant.materialMakerVariantIndex, variant]))
    },

    canRetry () {
      return this.hasRole(this.ROLES.SUPER_ADMIN) && ['FAILED', 'BLOCKED', 'READY'].includes(this.setStatus)
    },

    canApprove () {
      return this.hasRole(this.ROLES.SUPER_ADMIN) && this.setStatus === 'READY' && !this.isPublished
    },

    canUnpublish () {
      return this.hasRole(this.ROLES.SUPER_ADMIN) && this.isPublished
    }
  },

  methods: {
    variantFile (materialId) {
      const variant = this.variants.find(({ id }) => id === materialId)
      const pendingVersion = variant?.versions?.find(({ state }) => state === 'PENDING_REVIEW')
      return pendingVersion?.file || variant?.files?.[0]?.file
    },

    createAttachmentUrl ({ cdnUrl }) {
      const newUrl = new URL(cdnUrl)
      newUrl.searchParams.append('attachment', 'false')
      return newUrl.toString()
    },

    positionClass (position) {
      const variant = this.variantByPosition.get(position)
      if (!variant) return 'text-gray-400 border-gray-300'
      if (variant.status === 'READY') return 'text-green-700 border-green-400'
      if (variant.status === 'GENERATION_FAILED') return 'text-red-700 border-red-400'
      return 'text-gray-600 border-gray-400'
    },

    positionTitle (position) {
      const variant = this.variantByPosition.get(position)
      return variant
        ? `${variant.displayName} — ${variant.status}`
        : this.$t('components.landingpages.materialSet.missingVariant', { position })
    },

    /**
     * Tell the surrounding screen that work restarted.
     *
     * `refetchQueries` refreshes the list once; observation is what keeps it
     * refreshing. Retry in particular moves every variant back to a
     * non-terminal state, and polling had already stopped by then.
     */
    announceWorkStarted () {
      eventBus.$emit(eventNames.MATERIAL_WORK_STARTED, { companyId: this.companyId })
    },

    refetchAfterSetChange () {
      return [
        { query: MATERIALS, variables: { companyId: this.companyId } },
        ...(this.isExpanded
          ? [{
            query: GET_MATERIAL_SET,
            variables: { materialSetId: this.materialSetId, companyId: this.companyId }
          }]
          : [])
      ]
    },

    retrySet () {
      this.showConfirmationDialog({
        action: 'generate',
        message: this.$t('components.landingpages.materialSet.retryConfirm'),
        onConfirm: () => this.performRetrySet()
      })
    },

    async performRetrySet () {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: RETRY_MATERIAL_SET,
          variables: { materialSetId: this.materialSetId, companyId: this.companyId },
          refetchQueries: this.refetchAfterSetChange()
        })
        this.announceWorkStarted()
        this.showNotification('success', this.$t('components.landingpages.materialSet.retrySuccess', {
          count: data.retryMaterialSet.queuedCount
        }))
      } catch (err) {
        this.showNotification('error', this.$t('components.landingpages.materialSet.retryFailed', { error: err.message }))
      }
    },

    async approveSet () {
      try {
        await this.$apollo.mutate({
          mutation: APPROVE_MATERIAL_SET,
          variables: { materialSetId: this.materialSetId, companyId: this.companyId },
          refetchQueries: this.refetchAfterSetChange()
        })
        this.announceWorkStarted()
      } catch (err) {
        this.showNotification('error', this.$t('components.landingpages.materialSet.approveFailed', { error: err.message }))
      }
    },

    unpublishSet () {
      this.showConfirmationDialog({
        action: 'delete',
        message: this.$t('components.landingpages.materialSet.unpublishConfirm'),
        onConfirm: () => this.performUnpublishSet()
      })
    },

    async performUnpublishSet () {
      try {
        await this.$apollo.mutate({
          mutation: UNPUBLISH_MATERIAL_SET,
          variables: { materialSetId: this.materialSetId, companyId: this.companyId },
          refetchQueries: this.refetchAfterSetChange()
        })
        this.announceWorkStarted()
      } catch (err) {
        this.showNotification('error', this.$t('components.landingpages.materialSet.unpublishFailed', { error: err.message }))
      }
    }
  }
}
</script>
